import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { message, Table, Spin, Tooltip, Tag, Space } from 'antd';
import styled from 'styled-components';
import { MakeApiCall } from '../../../../apis';
import { ConvertParams } from '../../../../config';

// --- CATEGORY FILTER CONFIGURATION ---
const METRIC_CATEGORIES = {
  SALES: {
    label: 'Sales Metrics',
    color: '#e8fff3',
    textColor: '#50cd89',
    keys: ['sales.total', 'sales.organic', 'sales.sp_sales', 'sales.sb_sales', 'sales.sd_sales', 'total_orders', 'units.total', 'units.organic', 'units.sp_units', 'units.sb_units', 'units.sd_units', 'refund_units', 'sellable_returns']
  },
  ADS: {
    label: 'Ads Metrics',
    color: '#f1faff',
    textColor: '#009ef7',
    keys: ['advertising_clicks.total', 'advertising_clicks.sp_click', 'advertising_clicks.sb_click', 'advertising_clicks.sd_click', 'ad_cvr', 'advertising_cost.total', 'advertising_cost.sp_cost', 'advertising_cost.sb_cost', 'advertising_cost.sd_cost', 'advertising_sales.total', 'advertising_sales.sp_sales', 'advertising_sales.sb_sales', 'advertising_sales.sd_sales']
  },
  COSTS: {
    label: 'Costs',
    color: '#fff5f8',
    textColor: '#f1416c',
    keys: ['amazon_fees.total', 'amazon_fees.fba_per_unit_fulfilment_fee', 'amazon_fees.fba_weight_based_fee', 'amazon_fees.referral_fee', 'amazon_fees.fixed_closing_fee', 'amazon_fees.shipping_credits', 'amazon_fees.promotional_rebates', 'amazon_fees.item_TDS', 'cost_of_goods.total', 'refund_cost.total', 'expenses.total', 'tax_liability.total', 'tax_liability.total_tax_collected', 'tax_liability.total_tax_paid']
  },
  PROFITS: {
    label: 'Profits',
    color: '#f8f5ff',
    textColor: '#7239ea',
    keys: ['gross_profit', 'net_profit']
  },
};

// --- ENHANCED STYLED COMPONENTS ---
const Wrapper = styled.div`
  .card-custom {
    border-radius: 12px;
    border: 1px solid #eff2f5;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  }

  /* Filter Tag Styling */
  .filter-tag {
    cursor: pointer;
    padding: 6px 14px;
    border-radius: 6px;
    font-weight: 600;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    user-select: none;

    &:hover { opacity: 0.85; transform: translateY(-1px); }
    &.inactive {
      background: #f5f8fa !important;
      color: #a1a5b7 !important;
      border-color: #e4e6ef;
      opacity: 0.6;
    }
  }

  /* Full Row Color Coding */
  .row-sales .ant-table-cell { background-color: #e8fff3 !important; }
  .row-ads .ant-table-cell { background-color: #f1faff !important; }
  .row-costs .ant-table-cell { background-color: #fff5f8 !important; }
  .row-profits .ant-table-cell { background-color: #f8f5ff !important; }

  .ant-table-cell {
    position: relative;
    transition: background 0.3s ease;
  }

  /* Slightly darken the category color on hover for feedback */
  .ant-table-tbody > tr:hover > td {
    filter: brightness(0.98);
  }

  .trend-box {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
  }

  .trend-arrow {
    font-size: 10px;
    padding: 2px;
    border-radius: 4px;
    &.up { color: #50cd89; background: #e8fff3; }
    &.down { color: #f1416c; background: #fff5f8; }
  }

  .ant-table-row-level-1 td:first-child {
    padding-left: 48px !important;
    position: relative;
    color: #7e8299;
    font-weight: 400;
  }

  .ant-table-row-level-1 td:first-child::before {
    content: "";
    position: absolute;
    left: 30px;
    top: 0;
    bottom: 55%;
    width: 12px;
    border-left: 1.5px solid #e4e6ef;
    border-bottom: 1.5px solid #e4e6ef;
    border-bottom-left-radius: 4px;
  }

  .parent-row-bold {
    td {
      font-weight: 700 !important;
      color: #181c32 !important;
    }
  }

  .total-column-sticky {
    font-weight: 700 !important;
    border-left: 2px solid #eff2f5 !important;
  }
`;

const renderTrendValue = (current, previous, formattedValue) => {
  if (typeof current !== 'number' || typeof previous !== 'number') return formattedValue;
  const isUp = current > previous;
  const isEqual = current === previous;

  return (
      <div className="trend-box">
        {!isEqual && (
            <Tooltip title={isUp ? "Increased" : "Decreased"}>
              <span className={`trend-arrow ${isUp ? 'up' : 'down'}`}>
                {isUp ? '▲' : '▼'}
              </span>
            </Tooltip>
        )}
        <span>{formattedValue}</span>
      </div>
  );
};

const PL_CONFIG = [
  { key: 'sales.total', label: 'Total Sales', isParent: true },
  { key: 'sales.organic', label: 'Organic Sales' },
  { key: 'sales.sp_sales', label: 'SP Sales' },
  { key: 'sales.sb_sales', label: 'SB Sales' },
  { key: 'sales.sd_sales', label: 'SD Sales' },
  { key: 'total_orders', label: 'Total Orders' },
  { key: 'units.total', label: 'Total Units', isParent: true },
  { key: 'units.organic', label: 'Organic Units' },
  { key: 'units.sp_units', label: 'SP Units' },
  { key: 'units.sb_units', label: 'SB Units' },
  { key: 'units.sd_units', label: 'SD Units' },
  { key: 'refund_units', label: 'Refund Units' },
  { key: 'sellable_returns', label: 'Sellable Returns' },
  { key: 'advertising_clicks.total', label: 'Ad Clicks' , isParent: true },
  { key: 'advertising_clicks.sp_click', label: 'SP Clicks' },
  { key: 'advertising_clicks.sb_click', label: 'SB Clicks' },
  { key: 'advertising_clicks.sd_click', label: 'SD Clicks' },
  { key: 'ad_cvr', label: 'Ad CVR' },
  { key: 'advertising_cost.total', label: 'Ad Cost' , isParent: true },
  { key: 'advertising_cost.sp_cost', label: 'SP Cost' },
  { key: 'advertising_cost.sb_cost', label: 'SB Cost' },
  { key: 'advertising_cost.sd_cost', label: 'SD Cost' },
  { key: 'advertising_sales.total', label: 'Ad Sales' , isParent: true },
  { key: 'advertising_sales.sp_sales', label: 'SP Sales' },
  { key: 'advertising_sales.sb_sales', label: 'SB Sales' },
  { key: 'advertising_sales.sd_sales', label: 'SD Sales' },
  { key: 'amazon_fees.total', label: 'Amazon Fees', isParent: true },
  { key: 'amazon_fees.fba_per_unit_fulfilment_fee', label: 'FBA Per Unit Fee' },
  { key: 'amazon_fees.fba_weight_based_fee', label: 'FBA Weight Fee' },
  { key: 'amazon_fees.referral_fee', label: 'Referral Fee' },
  { key: 'amazon_fees.fixed_closing_fee', label: 'Fixed Closing Fee' },
  { key: 'amazon_fees.shipping_credits', label: 'Shipping Credits' },
  { key: 'amazon_fees.promotional_rebates', label: 'Promotional Rebates' },
  { key: 'amazon_fees.item_TDS', label: 'Item TDS' },
  { key: 'cost_of_goods.total', label: 'COGS' },
  { key: 'refund_cost.total', label: 'Refund Cost' },
  { key: 'expenses.total', label: 'Expenses' },
  { key: 'tax_liability.total', label: 'Tax Liability', isParent: true },
  { key: 'tax_liability.total_tax_collected', label: 'Tax Collected' },
  { key: 'tax_liability.total_tax_paid', label: 'Tax Paid' },
  { key: 'gross_profit', label: 'Gross Profit' },
  { key: 'net_profit', label: 'Net Profit' }
];

const formatValue = (value, key, currencySign) => {
  if (value === null || value === undefined) return '-';
  const percentageFields = ['unit_session_percent', 'real_acos', 'refunds_percent', 'sellable_returns', 'margin', 'ad_cvr'];
  const integerFields = ['units', 'total_orders', 'sessions', 'active_subscriptions', 'advertising_clicks', 'refund_units'];
  const cleanKey = String(key).toLowerCase();

  let formatted = '';
  let isNegative = false;

  if (cleanKey.endsWith('roi')) {
    const num = Number(value);
    isNegative = num < 0;
    formatted = `${isNegative ? '-' : ''}${currencySign}${Math.abs(num).toFixed(2)}`;
  } else if (percentageFields.some(field => cleanKey.endsWith(field))) {
    const num = parseFloat(value);
    isNegative = num < 0;
    formatted = cleanKey.includes('ad_cvr') ? `${num.toFixed(2)}%` : `${(num * 100).toFixed(0)}%`;
  } else if (integerFields.some(field => cleanKey.endsWith(field) || cleanKey.includes('units') || cleanKey.includes('click'))) {
    isNegative = value < 0;
    formatted = parseInt(value).toLocaleString();
  } else if (typeof value === 'number') {
    isNegative = value < 0;
    formatted = `${isNegative ? '-' : ''}${currencySign}${Math.abs(value).toFixed(2)}`;
  } else {
    formatted = value;
  }

  return isNegative ? <span style={{ color: '#f1416c' }}>{formatted}</span> : formatted;
};

const isBoldRow = (record) => {
  const key = record.key;
  return (
      record._isParentConfig ||
      !key.includes('.') ||
      key.endsWith('.total') ||
      key === 'gross_profit' ||
      key === 'net_profit'
  );
};

const flattenData = (obj, prefix = '') => {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(acc, flattenData(val, path));
    } else {
      acc[path] = val;
    }
    return acc;
  }, {});
};

const useProfitAndLossData = (filters, token, selectedCurrencySign) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const buildTable = useCallback((data) => {
    if (!data?.length) { setColumns([]); setRows([]); return; }

    const uniqueDates = Array.from(new Set(data.map(entry => entry.report_date))).filter(Boolean);
    const dateKeys = uniqueDates.sort((a, b) => Date.parse(b) - Date.parse(a));

    const dynamicColumns = [
      {
        title: 'Parameter',
        dataIndex: 'parameter',
        key: 'parameter',
        fixed: 'left',
        width: 260,
        render: (text, record) => (
            <span style={{ fontWeight: isBoldRow(record) ? '700' : '400' }}>{text}</span>
        ),
      },
      ...dateKeys.map((date, index) => ({
        title: date,
        dataIndex: date,
        key: date,
        align: 'right',
        width: 150,
        render: (value, record) => {
          const nextDateKey = dateKeys[index + 1];
          const previousValue = record[nextDateKey];
          const formatted = formatValue(value, record.key, selectedCurrencySign);
          return renderTrendValue(value, previousValue, formatted);
        },
      })),
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        width: 160,
        fixed: 'right',
        className: 'total-column-sticky',
        render: (value, record) => formatValue(value, record.key, selectedCurrencySign),
      },
    ];

    const dateMap = data.reduce((acc, item) => {
      acc[item.report_date] = flattenData(item);
      return acc;
    }, {});

    const isPercentageKey = (key) => ['refunds_percent', 'margin', 'unit_session_percent', 'sellable_returns', 'ad_cvr'].some(k => key?.endsWith(k));

    const rowList = PL_CONFIG.map(config => {
      const row = { key: config.key, parameter: config.label, _isParentConfig: config.isParent };
      let sum = 0, count = 0;

      dateKeys.forEach(date => {
        let val;
        const entry = dateMap[date];
        if (config.key === 'ad_cvr') {
          const adClicks = entry?.['advertising_clicks.total'] || 0;
          const adUnits = (entry?.['units.sp_units'] || 0) + (entry?.['units.sb_units'] || 0) + (entry?.['units.sd_units'] || 0);
          val = adClicks > 0 ? (adUnits / adClicks) * 100 : 0;
        } else {
          val = entry?.[config.key];
        }
        row[date] = val;
        if (typeof val === 'number') { sum += val; count++; }
      });

      if (config.key === 'ad_cvr') {
        let totalClicks = 0, totalAdUnits = 0;
        dateKeys.forEach(date => {
          const entry = dateMap[date];
          totalClicks += (entry?.['advertising_clicks.total'] || 0);
          totalAdUnits += (entry?.['units.sp_units'] || 0) + (entry?.['units.sb_units'] || 0) + (entry?.['units.sd_units'] || 0);
        });
        row.total = totalClicks > 0 ? (totalAdUnits / totalClicks) * 100 : 0;
      } else {
        row.total = isPercentageKey(config.key) && count > 0 ? sum / count : sum;
      }
      return row;
    });

    const finalRows = [];
    const parentLookup = {};

    rowList.forEach(row => {
      if (row._isParentConfig) {
        row.children = [];
        const prefix = row.key.split('.')[0];
        parentLookup[prefix] = row;
        finalRows.push(row);
      } else {
        const prefix = row.key.split('.')[0];
        if (row.key.includes('.') && parentLookup[prefix]) {
          parentLookup[prefix].children.push(row);
        } else {
          finalRows.push(row);
        }
      }
    });

    setColumns(dynamicColumns);
    setRows(finalRows);
  }, [selectedCurrencySign]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await MakeApiCall(`dashboard/profit-and-loss/get-summary-data${ConvertParams(filters)}`, 'get', null, true, {}, token);
      if (res?.status) buildTable(res.data?.summaryData || []);
      else { message.warning(res?.message); setColumns([]); setRows([]); }
      setLoading(false);
    };
    fetchData();
  }, [filters, token, buildTable]);

  return { columns, rows, loading };
};

const PL = ({ periodicity, otherRange, userData, selectedMarketplace, selectedCurrencySign, selectedSku }) => {
  const [filters, setFilters] = useState({
    page: 1, 'per-page': 20, marketplace_id: selectedMarketplace?.id,
    report_type: periodicity, start_date: otherRange.start_date, end_date: otherRange.end_date, sku_lists: selectedSku?.join(",")
  });

  const [activeCategories, setActiveCategories] = useState(['SALES', 'ADS', 'COSTS', 'PROFITS']);

  useEffect(() => {
    setFilters(prev => ({
      ...prev, marketplace_id: selectedMarketplace?.id, report_type: periodicity,
      start_date: otherRange.start_date, end_date: otherRange.end_date, sku_lists: selectedSku?.join(",")
    }));
  }, [selectedMarketplace, periodicity, otherRange, selectedSku]);

  const { columns, rows, loading } = useProfitAndLossData(filters, userData?.token, selectedCurrencySign);

  const filteredRows = useMemo(() => {
    if (activeCategories.length === 4) return rows;

    const allowedKeys = activeCategories.flatMap(cat => METRIC_CATEGORIES[cat].keys);

    return rows.reduce((acc, row) => {
      const isParentAllowed = allowedKeys.includes(row.key);
      const filteredChildren = row.children ? row.children.filter(child => allowedKeys.includes(child.key)) : [];

      if (isParentAllowed || filteredChildren.length > 0) {
        acc.push({
          ...row,
          children: filteredChildren.length > 0 ? filteredChildren : undefined
        });
      }
      return acc;
    }, []);
  }, [rows, activeCategories]);

  const toggleCategory = (catKey) => {
    setActiveCategories(prev =>
        prev.includes(catKey) ? prev.filter(k => k !== catKey) : [...prev, catKey]
    );
  };

  // Helper to determine row class
  const getRowClassName = (record) => {
    let classes = [];
    if (isBoldRow(record)) classes.push('parent-row-bold');

    if (METRIC_CATEGORIES.SALES.keys.includes(record.key)) classes.push('row-sales');
    else if (METRIC_CATEGORIES.ADS.keys.includes(record.key)) classes.push('row-ads');
    else if (METRIC_CATEGORIES.COSTS.keys.includes(record.key)) classes.push('row-costs');
    else if (METRIC_CATEGORIES.PROFITS.keys.includes(record.key)) classes.push('row-profits');

    return classes.join(' ');
  };

  return (
      <Wrapper className="p-4">
        <div className="card-custom">
          <div className="d-flex justify-content-between align-items-center px-8 py-6 border-bottom">
            <h3 className="m-0 fw-bold text-dark">Profit & Loss</h3>

            <Space size="middle">
              {Object.entries(METRIC_CATEGORIES).map(([key, config]) => (
                  <Tag
                      key={key}
                      className={`filter-tag ${activeCategories.includes(key) ? '' : 'inactive'}`}
                      style={{
                        background: activeCategories.includes(key) ? config.color : '',
                        color: activeCategories.includes(key) ? config.textColor : ''
                      }}
                      onClick={() => toggleCategory(key)}
                  >
                    {config.label}
                  </Tag>
              ))}
            </Space>
          </div>

          <div className="p-2">
            {loading ? (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                  <Spin size="large" />
                </div>
            ) : filteredRows?.length > 0 ? (
                <Table
                    scroll={{ x: 'max-content', y: 700 }}
                    columns={columns}
                    dataSource={filteredRows}
                    rowKey="key"
                    pagination={false}
                    expandable={false}
                    rowClassName={getRowClassName}
                />
            ) : (
                <div className="text-center py-20">
                  <img src="/assets/media/no-data.png" alt="Empty" style={{ width: '150px' }} />
                  <h5 className="text-muted mt-5">No metrics selected or available</h5>
                </div>
            )}
          </div>
        </div>
      </Wrapper>
  );
};

export default PL;